import { Box, Tab, Typography } from '@mui/material';
import { useConfig, useNotification, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { useLocations } from 'apps/wrapper/src/hooks/use-locations';
import { ContactApi } from 'apps/wrapper/src/services/api-client/contact-api';
import moment from 'moment';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEventBus } from '../../../../hooks/use-event-bus';
import MilaService from '../../../../services/mila-service/mila-service';
import { MilaCase, MilaMessage, MilaSender } from '../../../../services/mila-service/types';
import { CloseDialogButton, DialogContentWrapper, DialogTitle } from '../../../shared/dialog/dialog.styles';
import { MilaModalSkeleton } from './mila-modal.skeleton';
import { MilaSection } from './mila-section';
import {
  DialogContent,
  MessageContainer,
  MessageContent,
  MessageTimestamp,
  MilaDialog,
  PatientInfoText,
  SenderLabel,
  StyledTabs
} from './mila.styles';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`mila-tabpanel-${index}`}
      aria-labelledby={`mila-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const getCommunicationChannelLabel = (channel?: MilaCase['communicationChannel']): string | undefined => {
  switch (channel) {
    case 'CHAT':
      return 'Chat';
    case 'VOICE':
      return 'Voice';
    case 'EMAIL':
      return 'Email';
    default:
      return channel;
  }
};

export const MilaCaseModal: FC = () => {
  const { config } = useConfig();
  const { oidcService } = useOidcWithAdmin();
  const { showError } = useNotification();
  const { listen } = useEventBus();
  const [isLoading, setIsLoading] = useState(false);
  const [milaCase, setMilaCase] = useState<MilaCase | null>(null);
  const [patientName, setPatientName] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const { selectedLocation } = useLocations();
  const contactApi = useMemo(() => ContactApi.getInstance(), []);
  const [isOpen, setIsOpen] = useState(false);

  const onClose = () => {
    setIsOpen(false);
    setTabValue(0);
    setMilaCase(null);
    setPatientName(null);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const milaService = useMemo(() => MilaService.getInstance(config, oidcService), [config, oidcService]);

  const loadCase = useCallback(
    async (caseId: string, patientId?: number) => {
      if (!selectedLocation) {
        showError('No location selected or case ID missing');
        return;
      }

      try {
        setIsLoading(true);

        const caseData = await milaService.getMilaCase(selectedLocation.id, caseId);

        setMilaCase(caseData);

        try {
          const id = caseData.contactId || patientId;

          if (typeof id === 'number') {
            const patientInfo = await contactApi.getContactDetailsById(id);

            setPatientName(`${patientInfo.firstName} ${patientInfo.lastName}`);
          }
        } catch (error) {
          console.error('Error fetching patient details:', error);
          showError('Unable to fetch patient details');
        }
      } catch (error) {
        console.error('Error loading transcript:', error);
        showError('Unable to load voice transcript');
      } finally {
        setIsLoading(false);
      }
    },
    [selectedLocation, milaService, contactApi, showError]
  );

  useEffect(
    () =>
      listen('milaDialog', (event: any) => {
        const data = event.data as { type: string; caseId: string; patientId?: number };

        if (data.type === 'view-case' && data.caseId) {
          loadCase(data.caseId, data.patientId);
          setIsOpen(true);
        }
      }),
    [listen, loadCase]
  );

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const sortedMessages = useMemo(
    () =>
      milaCase?.messages
        ? [...milaCase.messages].sort((a, b) => moment(a.time).valueOf() - moment(b.time).valueOf())
        : [],
    [milaCase]
  );

  const scrollToTop = useCallback(() => {
    const el = scrollContainerRef.current;

    if (el) {
      el.scrollTop = 0;
    }
  }, []);

  useEffect(() => {
    if (isOpen && !isLoading && tabValue === 0 && sortedMessages.length > 0) {
      scrollToTop();
    }
  }, [isOpen, isLoading, tabValue, sortedMessages.length, scrollToTop]);

  if (!isOpen) return null;

  const getDateRange = () => {
    if (milaCase?.timeOfCreation && milaCase?.timeOfCompletion) {
      return `${moment(milaCase.timeOfCreation).format('MM/DD/YYYY')} - ${moment(milaCase.timeOfCompletion).format(
        'MM/DD/YYYY'
      )}`;
    }

    if (milaCase?.timeOfCompletion) {
      return moment(milaCase.timeOfCompletion).format('MM/DD/YYYY');
    }

    return '';
  };

  return (
    <MilaDialog open={isOpen}>
      {isLoading ? (
        <MilaModalSkeleton onClose={onClose} />
      ) : (
        <DialogContentWrapper>
          <DialogTitle sx={{ cursor: 'default' }}>
            {getCommunicationChannelLabel(milaCase?.communicationChannel)} Transcript -{' '}
            {milaCase?.caseType || 'Conversation'}
            <PatientInfoText>
              {patientName}: {getDateRange()}
            </PatientInfoText>
            <CloseDialogButton onClick={onClose} />
          </DialogTitle>

          <StyledTabs value={tabValue} onChange={handleTabChange} textColor="inherit">
            <Tab label="TRANSCRIPT" />
            <Tab label="ADDITIONAL INFO" />
          </StyledTabs>

          <TabPanel value={tabValue} index={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '505px' }}>
              <Box sx={{ flexShrink: 0 }}>
                {milaCase?.summary && <MilaSection title="Summary" content={milaCase.summary} />}
                {milaCase?.notes && milaCase?.communicationChannel === 'VOICE' && (
                  <MilaSection title="Note" content={milaCase.notes} />
                )}
              </Box>
              <DialogContent ref={scrollContainerRef} sx={{ flex: 1, maxHeight: 'none', minHeight: 0, mt: 2 }}>
                <div>
                  {sortedMessages.map((message: MilaMessage, index: number) => (
                    <MessageContainer key={`message-mila-${String(index)}`}>
                      {message.sender === MilaSender.Mila && <SenderLabel>Agent</SenderLabel>}
                      <MessageContent isAgent={message.sender === MilaSender.Mila}>
                        {message.message}
                        <MessageTimestamp>{moment(message.time).format('MM/DD/YYYY')}</MessageTimestamp>
                      </MessageContent>
                    </MessageContainer>
                  ))}
                </div>
              </DialogContent>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <DialogContent>
              <div style={{ position: 'relative' }}>
                {milaCase ? (
                  <>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Case Details
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Case ID:</strong> {milaCase.caseId}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Case Type:</strong> {milaCase.caseType}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Outcome:</strong> {milaCase.caseOutcome}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Created:</strong>{' '}
                        {milaCase.timeOfCreation ? moment(milaCase.timeOfCreation).format('MM/DD/YYYY h:mm A') : 'N/A'}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Completed:</strong> {moment(milaCase.timeOfCompletion).format('MM/DD/YYYY h:mm A')}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Typography variant="body1">No additional information available for this transcript.</Typography>
                )}
              </div>
            </DialogContent>
          </TabPanel>
        </DialogContentWrapper>
      )}
    </MilaDialog>
  );
};
